import type {
  PerformanceTracker,
  PerformanceMetrics,
  LoggerContext,
  Timer as TimerInterface,
} from "./types";
import type { Logger } from "./logger";

export class DefaultPerformanceTracker implements PerformanceTracker {
  private metrics: PerformanceMetrics[] = [];
  private logger: Logger;

  constructor(logger: Logger) {
    this.logger = logger;
  }

  startTimer(operation: string, context?: LoggerContext): TimerInterface {
    return new Timer(operation, this.logger, context);
  }

  async measureAsync<T>(
    operation: string,
    fn: () => Promise<T>,
    context?: LoggerContext,
  ): Promise<T> {
    const timer = this.startTimer(operation, context);

    try {
      const result = await fn();
      const metrics = timer.end(true);
      this.metrics.push(metrics);
      return result;
    } catch (error) {
      const metrics = timer.end(false);
      this.metrics.push(metrics);
      throw error;
    }
  }

  measureSync<T>(operation: string, fn: () => T, context?: LoggerContext): T {
    const timer = this.startTimer(operation, context);

    try {
      const result = fn();
      const metrics = timer.end(true);
      this.metrics.push(metrics);
      return result;
    } catch (error) {
      const metrics = timer.end(false);
      this.metrics.push(metrics);
      throw error;
    }
  }

  getMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  clearMetrics(): void {
    this.metrics = [];
  }
}

export class Timer implements TimerInterface {
  public readonly operation: string;
  public readonly startTime: number;
  public readonly context?: LoggerContext;
  private logger: Logger;

  constructor(operation: string, logger: Logger, context?: LoggerContext) {
    this.operation = operation;
    this.startTime = performance.now();
    this.context = context;
    this.logger = logger;
  }

  end(success: boolean = true): PerformanceMetrics {
    const endTime = performance.now();
    const duration = endTime - this.startTime;

    const metrics: PerformanceMetrics = {
      operation: this.operation,
      duration,
      startTime: this.startTime,
      endTime,
      success,
      context: this.context,
    };

    const logLevel = success ? "info" : "error";
    const logMessage = success
      ? `Operation ${this.operation} completed`
      : `Operation ${this.operation} failed`;

    this.logger[logLevel](logMessage, {
      ...this.context,
      duration,
      success,
      operation: this.operation,
    });

    return metrics;
  }
}
